import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { adminApi } from '@/api';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  ErrorState,
  LoadingState,
  PageHeader,
  Pagination,
  StatTile,
  useToast,
} from '@/components/ui';
import { connectionLabel, connectionTone, stageLabel } from '@/features/ats/stages';
import { SeamSettings } from '@/features/integrations/SeamSettings';
import { errorMessage } from '@/hooks/useApiError';
import { formatDate } from '@/utils/format';

/**
 * The platform's view of ATS integrations.
 *
 * It shows that a company is connected, to what, and how much is waiting — and
 * deliberately not *who* is waiting. Being able to run the handover job is not
 * a reason to be able to read somebody's pipeline, and the API does not return
 * it either.
 */
export function AdminIntegrationsPage() {
  return (
    <div>
      <PageHeader
        title="Third-party APIs"
        description="What the platform is wired to, and which companies send their candidates onwards."
      />

      <SeamSettings />

      {/* ATS handover is not offered to companies for now, so its admin
          section (counters, "Run now", connectors and connected companies) is
          hidden. Restore the line below to bring it back.
      <AtsHandoverSection /> */}
    </div>
  );
}

/** Companies' ATS connections, and the button that runs the handovers. */
export function AtsHandoverSection() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [page, setPage] = useState(1);

  const providers = useQuery({
    queryKey: ['admin-ats-providers'],
    queryFn: adminApi.listAtsProviders,
  });
  const connections = useQuery({
    queryKey: ['admin-ats-connections', page],
    queryFn: () => adminApi.listAtsConnections({ page, page_size: 20 }),
  });

  const run = useMutation({
    mutationFn: adminApi.runAtsHandovers,
    onSuccess: (summary) => {
      toast.success(summary.message);
      queryClient.invalidateQueries({ queryKey: ['admin-ats-connections'] });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  if (connections.isError) {
    return (
      <ErrorState
        message={errorMessage(connections.error)}
        onRetry={() => void connections.refetch()}
      />
    );
  }

  const rows = connections.data?.data ?? [];
  const meta = connections.data?.meta;
  const automatic = rows.filter((row) => row.auto_export && row.automation_allowed).length;
  const waiting = rows.reduce((total, row) => total + row.pending_count, 0);

  return (
    <>
      <div className="mb-6 grid gap-4 sm:grid-cols-3">
        <StatTile label="Connected" value={meta?.total_items ?? 0} />
        <StatTile label="On automatic" value={automatic} />
        <StatTile label="Waiting to go" value={waiting} />
      </div>

      <Card className="mb-6">
        <CardHeader
          title="Run the handovers"
          description="Sends what every company with automatic handover has waiting. Nothing in a request path triggers it, so a company's candidates never depend on whether somebody opened a page."
        />
        <Button isLoading={run.isPending} onClick={() => run.mutate()}>
          Run now
        </Button>
      </Card>

      <Card className="mb-6">
        <CardHeader title="Connectors" description="What a company may connect to." />
        {providers.isPending ? (
          <LoadingState label="Loading connectors…" />
        ) : providers.isError ? (
          <Alert tone="error" title="Could not load the connectors">
            <p>{errorMessage(providers.error)}</p>
            <Button
              variant="secondary"
              size="xs"
              className="mt-2"
              onClick={() => void providers.refetch()}
            >
              Try again
            </Button>
          </Alert>
        ) : (
          <ul className="divide-line divide-y">
            {(providers.data ?? []).map((provider) => (
              <li key={provider.name} className="py-3">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="text-ink text-sm font-medium">{provider.label}</p>
                  {provider.produces_document && <Badge tone="neutral">Produces a file</Badge>}
                  {provider.fields.length === 0 && (
                    <Badge tone="success">No credentials needed</Badge>
                  )}
                </div>
                <p className="text-ink-muted mt-1 text-sm">{provider.description}</p>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card>
        <CardHeader title="Connected companies" />
        {connections.isPending ? (
          <LoadingState label="Loading connections…" />
        ) : rows.length === 0 ? (
          <EmptyState
            compact
            title="Nobody is connected yet"
            description="No company has linked an applicant tracking system."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table w-full text-sm">
              <thead>
                <tr className="text-ink-muted border-line border-b text-left">
                  <th scope="col" className="py-2 pr-4 font-medium">
                    Company
                  </th>
                  <th scope="col" className="py-2 pr-4 font-medium">
                    System
                  </th>
                  <th scope="col" className="py-2 pr-4 font-medium">
                    Plan
                  </th>
                  <th scope="col" className="py-2 pr-4 font-medium">
                    Sends
                  </th>
                  <th scope="col" className="py-2 pr-4 font-medium">
                    From
                  </th>
                  <th scope="col" className="py-2 pr-4 font-medium">
                    Waiting
                  </th>
                  <th scope="col" className="py-2 pr-4 font-medium">
                    Sent
                  </th>
                  <th scope="col" className="py-2 pr-4 font-medium">
                    Last
                  </th>
                  <th scope="col" className="py-2 font-medium">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="divide-line divide-y">
                {rows.map((row) => (
                  <tr key={row.company_id}>
                    <td className="text-ink py-3 pr-4">{row.company_name}</td>
                    <td className="text-ink-muted py-3 pr-4">{row.provider}</td>
                    <td className="text-ink-muted py-3 pr-4">{row.plan_code}</td>
                    <td className="text-ink-muted py-3 pr-4">
                      {!row.auto_export
                        ? 'On request'
                        : row.automation_allowed
                          ? 'Automatically'
                          : 'Paused — plan'}
                    </td>
                    <td className="text-ink-muted py-3 pr-4">{stageLabel(row.from_stage)}</td>
                    <td className="text-ink py-3 pr-4">{row.pending_count}</td>
                    <td className="text-ink-muted py-3 pr-4">{row.exported_count}</td>
                    <td className="text-ink-muted py-3 pr-4">
                      {row.last_export_at ? formatDate(row.last_export_at) : '—'}
                    </td>
                    <td className="py-3">
                      <Badge tone={connectionTone(row.status)}>{connectionLabel(row.status)}</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {meta && meta.total_pages > 1 && (
          <Pagination
            page={meta.page}
            totalPages={meta.total_pages}
            totalItems={meta.total_items}
            onPageChange={setPage}
          />
        )}
      </Card>
    </>
  );
}
